import React, { useState } from "react"
import Autocomplete from "@material-ui/lab/Autocomplete"
import {
	Grid,
	Typography,
	IconButton,
	Tooltip,
	TextField,
	Chip
} from "@material-ui/core"

import {
	Reply as AssignAttendanceIcon
} from "@material-ui/icons"

import {
	Divider, ActionDialog, Notification
} from "@/components"

import GenerateAttendanceSummaryButton from "@/components/Skynet/AttendanceSummary/AssignAttendance/GenerateAttendanceSummaryButton"
import AttendanceSummaryPreview from "@/components/Skynet/AttendanceSummary/AssignAttendance/AttendanceSummaryPreview"

import { useChatGlobalStateStore } from "@/store/ChatGlobalState"
import { useGlobalStateStore } from "@/store/GlobalState"

import useCustomMemo from "@/hooks/useCustomMemo"
import useSubscriptionLimits from "@/hooks/useSubscriptionLimits"

import { UserInInstanceRoleName } from "@/protocols/userInInstance"

import useStyles from "@/pages/Attendance/Chat/ConversationPanel/ConversationHeader/AssignAttendance/styles"
import useCustomStyles from "@/styles/custom"

import { AssignAttendanceData } from "@/protocols/channel"

type AttendanceFilterOption = {
	id?: number
	userId?: number
	teamId?: number
	name: string
	assignment_queue_type: AssignAttendanceData["assignmentQueueType"]
	category: "Atendentes" | "Equipes" | "Fila Geral"
	disableOptions?: {
		usersInTeamCount: {
			willValidate: boolean
			usersNumber: number | null
		}
	}
}
type AssignAttendanceProps = {
	disabled?: boolean
}

const AssignAttendance = (props: AssignAttendanceProps) => {
	const { disabled } = props

	const classes = useStyles()
	const customClasses = useCustomStyles()
	const chatGlobalStateStore = useChatGlobalStateStore()
	const globalStateStore = useGlobalStateStore()

	const [showEditAssignAttendanceDialog, setShowEditAssignAttendanceDialog] = useState<boolean>(false)
	const [actionDialogLoading, setActionDialogLoading] = useState<boolean>(false)
	const [assignAttendanceData, setAssignAttendanceData] = useState<AssignAttendanceData>({} as AssignAttendanceData)
	const [selectedAttendantOption, setSelectedAttendantOption] = useState<AttendanceFilterOption | null>(null)
	const [isSummaryIncluded, setIsSummaryIncluded] = useState<boolean>(false)

	const aiResourcesPermission = useSubscriptionLimits("permission", "ai_resources")
	const hasAIResourcesPermission = !aiResourcesPermission.mustBlock
	const userInstance = globalStateStore.user.user_instances.find(
		(userInstance) => userInstance.instance_id === globalStateStore.instance.instance_id
	)
	const userRoleName: UserInInstanceRoleName = userInstance?.user_role_name ?? "Atendente"

	const getTeamNameWithAttendants = (teamName: string, teamAttendantsCount: number): string => {
		const zeroAttendantsText = teamAttendantsCount === 0 ? ". Não é possível transferir" : ""
		const attendantsNumber = teamAttendantsCount === 1 ? (
			`${teamAttendantsCount} Atendente`
		) : (
			`${teamAttendantsCount} Atendentes${zeroAttendantsText}`
		)
		return `${teamName} (${attendantsNumber})`
	}

	const attendantFilterOptions: AttendanceFilterOption[] = chatGlobalStateStore.attendant.listOrderedAlphabetically
		.filter(attendant => attendant.type !== "virtual-attendant")
		.map(attendant => ({
			id: attendant.id,
			userId: attendant.id,
			name: attendant.name,
			assignment_queue_type: "User",
			category: "Atendentes",
			disableOptions: {
				usersInTeamCount: {
					willValidate: false,
					usersNumber: null
				}
			}
		}))

	const instanceTeams: AttendanceFilterOption[] = globalStateStore.instance.teams.map(team => ({
		id: team.id,
		teamId: team.id,
		name: getTeamNameWithAttendants(team.name, team.usersInTeamCount),
		assignment_queue_type: "Team",
		category: "Equipes",
		disableOptions: {
			usersInTeamCount: {
				willValidate: Boolean(team.usersInTeamCount === 0),
				usersNumber: team.usersInTeamCount
			}
		}
	}))
	const alphabeticallyOrderedInstanceTeams = instanceTeams.sort((oldTeam, newTeam) => oldTeam.name.localeCompare(newTeam.name))

	const generalQueueAssignOptions: AttendanceFilterOption[] = [
		{
			name: "Fila Geral",
			assignment_queue_type: "General",
			category: "Fila Geral"
		}
	]

	const allAssignOptions: AttendanceFilterOption[] = [...attendantFilterOptions, ...alphabeticallyOrderedInstanceTeams, ...generalQueueAssignOptions]

	const handleCloseEditAssignChatAttendance = () => {
		setShowEditAssignAttendanceDialog(false)
		setAssignAttendanceData({} as AssignAttendanceData)
		setSelectedAttendantOption(null)
		setIsSummaryIncluded(false)
	}

	const handleOpenEditAssignChatAttendance = () => {
		setShowEditAssignAttendanceDialog(true)
	}

	const handleChoosaAttendantToAssign = async (attendant: AttendanceFilterOption) => {
		setAssignAttendanceData({
			...assignAttendanceData,
			assignedUserId: attendant?.userId,
			assignedTeamId: attendant?.teamId,
			assignmentQueueType: attendant?.assignment_queue_type
		})
		setSelectedAttendantOption(attendant ?? null)
	}

	const handleChangeAssignmentObservation = async (assignmentObservation: string) => {
		setAssignAttendanceData({
			...assignAttendanceData,
			assignmentObservation: assignmentObservation
		})
	}

	const handleAssignChatAttendance = async () => {
		setActionDialogLoading(true)
		try {
			if (assignAttendanceData.assignedUserId || assignAttendanceData.assignedTeamId || assignAttendanceData.assignmentQueueType) {
				await chatGlobalStateStore.attendance.assignOnCurrentChat({
					...assignAttendanceData
				})
				handleCloseEditAssignChatAttendance()
			} else {
				Notification.error({ message: "Preencha o atendente que irá receber a transferência primeiro." })
			}
		} catch (error) {
			Notification.error({ message: "Houve um erro." })
		}
		setActionDialogLoading(false)
	}

	return useCustomMemo(() => (
		<>
			<Tooltip
				title="Transferir atendimento"
			>
				<IconButton
					disabled={disabled}
					onClick={handleOpenEditAssignChatAttendance}
				>
					<AssignAttendanceIcon
						className={`${classes.assignAttendanceIcon}`}
					/>
				</IconButton>
			</Tooltip>

			<ActionDialog
				title="TRANSFERIR ATENDIMENTO"
				onSave={handleAssignChatAttendance}
				saveText="TRANSFERIR"
				onClose={handleCloseEditAssignChatAttendance}
				loading={actionDialogLoading}
				openDialog={showEditAssignAttendanceDialog}
				fullWidth
				maxWidth="md"
			>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Typography
							color="textPrimary"
							className={customClasses.boldText}
						>
							Escolha o atendente, equipe responsável ou a Fila Geral
						</Typography>
						<Autocomplete
							options={allAssignOptions}
							getOptionSelected={(option, value) => option.id === value.id}
							getOptionLabel={(option) => option.name}
							getOptionDisabled={(option) => option.disableOptions?.usersInTeamCount.willValidate ?? false}
							groupBy={option => option.category}
							fullWidth
							filterSelectedOptions
							onChange={(_, attendants) => handleChoosaAttendantToAssign(attendants as AttendanceFilterOption)}
							renderInput={(params) => {
								return (
									<TextField
										className={classes.filterInput}
										variant="outlined"
										placeholder="Selecionar o atendente"
										{...({
											...params,
											InputProps: {
												...params.InputProps
											}
										})}
									/>
								)
							}}
							renderTags={(value, getTagProps) => {
								return value.map((attendant, index) => (
									<Chip
										key={index}
										label={attendant.name}
										size="small"
										{...getTagProps({ index })}
									/>
								))
							}
							}
							size="small"
						/>
					</Grid>
				</Grid>

				<Divider
					orientation="horizontal"
					size={2}
				/>

				<Grid container spacing={2}>
					<Grid item xs={12} container spacing={1}>
						<Grid item xs={12}>
							<Grid
								container
								alignItems="center"
								justifyContent="space-between"
							>
								<Grid item>
									<Typography
										color="textPrimary"
										className={customClasses.boldText}
									>
										Deseja inserir uma observação?
									</Typography>
								</Grid>
								<Grid item>
									<GenerateAttendanceSummaryButton
										hasAIResourcesPermission={hasAIResourcesPermission}
										userRoleName={userRoleName}
										onToggle={(includeSummary) => {
											setIsSummaryIncluded(includeSummary)
											setAssignAttendanceData({
												...assignAttendanceData,
												includeSummary
											})
										}}
									/>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<TextField
								value={assignAttendanceData.assignmentObservation}
								onChange={ ({ target }) => { handleChangeAssignmentObservation(target.value) } }
								className={classes.textareaLineNumbers}
								variant="outlined"
								color="primary"
								multiline
								fullWidth
								rows={6}
							/>
						</Grid>
						<Grid item xs={12}>
							<AttendanceSummaryPreview
								isSummaryIncluded={isSummaryIncluded}
								inboxChannelChatAttendanceId={chatGlobalStateStore.chat.current?.attendance?.id ?? 0}
							/>
						</Grid>
					</Grid>
				</Grid>
			</ActionDialog>
		</>
	), [
		showEditAssignAttendanceDialog,
		JSON.stringify(assignAttendanceData),
		chatGlobalStateStore.chat.current?.type,
		chatGlobalStateStore.chatListPanel.chatStatusFilter.current,
		hasAIResourcesPermission,
		isSummaryIncluded,
		selectedAttendantOption
	])
}

export default AssignAttendance
